import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Dumbbell } from "lucide-react";
import { useProgramImages, IllustrateAllButton, ProgramDayImage } from "@/components/ProgramImages";

export default function Programs() {
  const { t } = useLang();
  const [goal, setGoal] = useState("");
  const [days, setDays] = useState(4);
  const [level, setLevel] = useState("intermediate");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/programs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!goal.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/programs/generate", { goal, days_per_week: days, level });
      toast.success("Program generated");
      setOpen(data.id);
      await load();
    } catch {
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/programs/${id}`);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="programs-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">AI PROGRAMMING</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="programs-title">{t.programs.title}</h1>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <form onSubmit={generate} className="rounded-xl border border-[#1E2638] bg-[#121721] p-6 space-y-4">
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.programs.goal}</Label>
            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={t.programs.goalPh} data-testid="programs-goal-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.programs.days}</Label>
            <Input type="number" min={2} max={7} value={days} onChange={(e) => setDays(parseInt(e.target.value || 4))} data-testid="programs-days-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.programs.level}</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger data-testid="programs-level-select" className="mt-1 bg-[#090C10] border-[#1E2638] text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121721] border-[#1E2638] text-white">
                <SelectItem value="beginner">{t.programs.levels.beginner}</SelectItem>
                <SelectItem value="intermediate">{t.programs.levels.intermediate}</SelectItem>
                <SelectItem value="elite">{t.programs.levels.elite}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading || !goal.trim()} data-testid="programs-generate-btn" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
            <Dumbbell className="w-4 h-4 mr-2" />
            {loading ? t.programs.generating : t.programs.generate}
          </Button>
        </form>

        <div className="lg:col-span-2 space-y-4">
          <div className="font-display font-bold uppercase text-xl">{t.programs.library}</div>
          {items.length === 0 && <div className="text-slate-500 text-sm">—</div>}
          {items.map((p) => (
            <ProgramCard key={p.id} p={p} isOpen={open === p.id} onToggle={() => setOpen(open === p.id ? null : p.id)} onDelete={() => del(p.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ProgramCard({ p, isOpen, onToggle, onDelete }) {
  const prog = p.program || {};
  const days = Array.isArray(prog.days) ? prog.days : [];
  const { images, busy, illustrate } = useProgramImages(p.id, isOpen);
  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-5" data-testid={`program-${p.id}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-mono-tech tracking-wider text-emerald-400 uppercase">{p.level} · {p.days_per_week}D/W</div>
          <div className="font-display font-bold text-lg mt-1">{prog.title || p.goal}</div>
          <div className="text-sm text-slate-400 mt-1 line-clamp-2">{prog.summary}</div>
        </div>
        <div className="flex gap-2 shrink-0 ml-3">
          <Button variant="ghost" size="sm" onClick={onToggle} data-testid={`program-toggle-${p.id}`} className="text-emerald-400">
            {isOpen ? "Hide" : "View"}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} data-testid={`program-delete-${p.id}`} className="text-slate-400 hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {isOpen && (
        <div className="mt-4 space-y-4 border-t border-[#1E2638] pt-4">
          <IllustrateAllButton days={days} images={images} busy={busy} illustrate={illustrate} programId={p.id} />
          {days.map((d, i) => (
            <div key={i}>
              <div className="font-display font-bold uppercase text-sm text-[#00F076]">{d.day} · {d.focus}</div>
              <ProgramDayImage programId={p.id} dayIndex={i} image={images[i]} busy={busy[i]} onIllustrate={() => illustrate(i)} />
              <div className="mt-2 space-y-1">
                {(d.drills || []).map((dr, j) => (
                  <div key={j} className="text-xs font-mono-tech text-slate-300 flex flex-wrap gap-2">
                    <span className="text-white font-semibold">{dr.name}</span>
                    {dr.sets && <span className="text-slate-500">· {dr.sets}</span>}
                    {dr.reps && <span className="text-slate-500">· {dr.reps}</span>}
                    {dr.notes && <span className="text-slate-500">— {dr.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
